import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { TranscriptionDialogContext } from '../../../../../../context';
import { useNextStepAfterUpdate } from '../../Wizard/Wizard.hooks';
import { instrumentsConfig } from '../Instrument.config';
import { ArrowIcon } from './ArrowIcon';
import { TranscriptionModeInstrumentsSet } from './TranscriptionModeInstrumentsSet';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5em',
    padding: '1em',
    [theme.breakpoints.down('sm')]: {
      padding: '0',
    },
  },
  optionsContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    width: '100%',
    maxWidth: '620px',
  },
  option: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '1em',
    border: '1px solid #fff',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: `0px 0px 5px 0 ${theme.palette.primary.main}`,
      borderColor: theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  iconsContainer: {
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    flexShrink: 0,
  },
  textContent: {
    flex: 1,
    textAlign: 'left',
  },
  iconsContent: {
    display: 'flex',
    alignItems: 'center',
  },
  optionTitle: {
    fontSize: '1em',
    fontWeight: 500,
    color: '#fff',
    marginBottom: '0.5em',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '0',
    },
  },
  optionDescription: {
    fontSize: '0.875em',
    color: '#d1cfcf',
    lineHeight: '1.4',
    [theme.breakpoints.down('sm')]: {
      marginTop: '0.5em',
    },
  },
  instrumentComponent: {
    padding: '0 2em 0 3.4em',
  },
}));

export interface TranscriptionModeProps {}

const TranscriptionMode: React.FC<TranscriptionModeProps> = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { settings } = React.useContext(TranscriptionDialogContext);
  const next = useNextStepAfterUpdate();
  const instrument = settings.instruments?.[0];
  const instrumentComponent = instrumentsConfig[instrument]?.component;
  const handleOptionClick = (mode: 'piano-only' | 'extract-piano' | 'arrangement') => {
    next({
      solo: mode === 'piano-only',
      arrangement: mode === 'arrangement',
    });
  };
  if (!instrument) {
    return null;
  }
  const options = [
    {
      id: 'piano-only',
      title: t('home.input.dialog.transcription_mode.instrument_only', {
        instrument: t(`home.input.dialog.choose_instrument.instruments.${instrument}`),
      }),
      description: t('home.input.dialog.transcription_mode.instrument_only_description', {
        instrument: t(`home.input.dialog.choose_instrument.instruments.${instrument}`),
      }),
      icons: (
        <div className={classes.iconsContainer}>
          <TranscriptionModeInstrumentsSet arrangement={false} singleInstrument />
        </div>
      ),
    },
    {
      id: 'extract-piano',
      title: t('home.input.dialog.transcription_mode.extract_instrument', {
        instrument: t(`home.input.dialog.choose_instrument.instruments.${instrument}`),
      }),
      description: t('home.input.dialog.transcription_mode.extract_instrument_description', {
        instrument: t(`home.input.dialog.choose_instrument.instruments.${instrument}`),
      }),
      icons: (
        <div className={classes.iconsContainer}>
          <TranscriptionModeInstrumentsSet arrangement={false} />
        </div>
      ),
    },
    {
      id: 'arrangement',
      title: t('home.input.dialog.transcription_mode.arrangement', {
        instrument: t(`home.input.dialog.choose_instrument.instruments.${instrument}`),
      }),
      description: t('home.input.dialog.transcription_mode.arrangement_description', {
        instrument,
      }),
      icons: (
        <div className={classes.iconsContainer}>
          <TranscriptionModeInstrumentsSet arrangement />
        </div>
      ),
    },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.optionsContainer}>
        {options.map((option) => (
          <button
            key={option.id}
            id={option.id}
            data-testid={option.id}
            className={classes.option}
            onClick={() =>
              handleOptionClick(option.id as 'piano-only' | 'extract-piano' | 'arrangement')}
            type="button"
          >
            <div className={classes.iconsContent}>
              {option.icons}
              <ArrowIcon />
              <span className={classes.instrumentComponent}>{instrumentComponent}</span>
            </div>
            <div className={classes.textContent}>
              <h3 className={classes.optionTitle}>{option.title}</h3>
              <p className={classes.optionDescription}>{option.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TranscriptionMode;
